import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../cn';
import { Spinner } from './spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

/*
 * Buttons are keys (Premium Retail): a white key with an edge and a lip, or a
 * filled key lit along its top. Pressing removes the lip and drops the key a
 * pixel — see `key` / `key-fill` in styles.css. Ghost and soft stay flat: they
 * are quiet actions, and a lip would promote them.
 */
const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-linear-to-b from-primary-top to-primary text-white key-fill hover:from-primary hover:to-primary-hover disabled:opacity-45',
  secondary: 'key text-ink hover:bg-sunken disabled:text-ink-4',
  ghost: 'text-ink-2 hover:bg-rail hover:text-ink active:bg-line disabled:text-ink-4',
  soft: 'bg-primary-soft text-primary-ink hover:bg-primary-line/60 active:bg-primary-line disabled:opacity-50',
  danger: 'bg-danger text-white key-fill hover:bg-danger-hover disabled:opacity-45',
  success:
    'bg-linear-to-b from-success-top to-success text-white key-fill hover:brightness-95 disabled:opacity-45',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-sm',
  md: 'h-10 px-4 text-base gap-2 rounded-md',
  lg: 'h-12 px-5 text-md gap-2 rounded-lg',
  xl: 'h-16 px-6 text-lg gap-3 rounded-lg font-semibold',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Icon rendered before the label. */
  icon?: ReactNode;
  /** Keyboard shortcut hint, e.g. "F12". */
  shortcut?: string;
  asChild?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    shortcut,
    asChild,
    block,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = cn(
    'relative inline-flex select-none items-center justify-center font-medium whitespace-nowrap',
    'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out',
    'active:translate-y-px disabled:pointer-events-none',
    variants[variant],
    sizes[size],
    block && 'w-full',
    className,
  );
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} {...rest}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
      {shortcut && (
        <kbd
          className={cn(
            'ml-auto rounded-xs px-1.5 py-0.5 font-sans text-2xs font-medium tnum',
            variant === 'primary' || variant === 'danger' || variant === 'success'
              ? 'bg-white/18 text-white'
              : 'bg-sunken text-ink-3 shadow-[inset_0_0_0_1px_var(--color-key-edge)]',
          )}
        >
          {shortcut}
        </kbd>
      )}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
}

const iconSizes = { sm: 'size-8 rounded-sm', md: 'size-10 rounded-md', lg: 'size-12 rounded-md' };

/** Square icon-only button; `label` becomes the accessible name and tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40',
        variants[variant],
        iconSizes[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
